import { Modal, ModalBody, Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IconTrash2 from "@/icons/IconTrash2";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import IconCheckMark from "@/icons/IconCheckMark";
import { IconClose } from "@velaro/velaro-component-lib";
import { AlertTriangle } from "@velaro/velaro-component-lib";
import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { useNavigate } from "react-router-dom";
import useToastAlert from "@/hooks/useToastAlert";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  token: string;
  tokenSecret: string;
  restletUrl: string;
  teamId: number;
}

interface Props {
  configs: NetSuiteConfig[];
  deleteConfig(id: number): void;
  addConfig(): void;
  teamId: number;
  onChange(id: number, field: keyof NetSuiteConfig, value: string): void;
  onSelectConfig(id: number): void;
  onCreateMode?: boolean;
}

export default function NetSuiteAccounts(props: Props) {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const api = useApi();
  const [company, setCompany] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [token, setToken] = useState("");
  const [tokenSecret, setTokenSecret] = useState("");
  const [restletUrl, setRestletUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const [selectedConfigId, setSelectedConfigId] = useState(
    props.configs.length === 1 ? props.configs[0].id : 0
  );
  const [selectedConfig, setSelectedConfig] = useState<NetSuiteConfig | null>(
    props.configs.length > 0 ? props.configs[0] : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    displayName: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const tokenSecretInputRef = useRef<HTMLInputElement>(null);
  const restletUrlInputRef = useRef<HTMLInputElement>(null);
  const [showToken, setShowToken] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tokenFocused, setTokenFocused] = useState(false);
  const [tokenSecretFocused, setTokenSecretFocused] = useState(false);
  const [displayNameFocused, setDisplayNameFocused] = useState(false);
  const [restletUrlFocused, setRestletUrlFocused] = useState(false);
  const toast = useToastAlert();
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    token: "",
    tokenSecret: "",
    restletUrl: ""
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (props.configs.length > 0) {
      if (selectedConfigId) {
        const existingConfig = props.configs.find(
          (c) => c.id === selectedConfigId
        );
        if (existingConfig) {
          setSelectedConfig(existingConfig);
          return;
        }
      }

      const lastConfig = props.configs[props.configs.length - 1];
      setSelectedConfigId(lastConfig.id);
      setSelectedConfig(lastConfig);
      props.onSelectConfig(lastConfig.id);
    } else {
      setSelectedConfig(null);
    }
  }, [props.configs]);

  const handleSelect = (config: NetSuiteConfig) => {
    if (editingId !== config.id) {
      setSelectedConfig(config);
      setSelectedConfigId(config.id);
      props.onSelectConfig(config.id);
      setIsOpen(false);
    }
  };

  const handleDeleteClick = (id: number, displayName: string) => {
    setConfigToDelete({ id, displayName });
    setIsModalOpen(true);
  };

  const confirmDelete = async (id?: number) => {
    if (!id) return;
    await api.messaging.delete(`NetSuite/Integrations/${id}`);
    props.deleteConfig(id);
    setIsModalOpen(false);
    setConfigToDelete(null);
  };

  const maskValue = (value: string) => {
    if (!value) return "";
    return "•".repeat(value.length);
  };

  const partiallyMaskValue = (value: string) => {
    if (!value) return "";
    if (value.length <= 8) return "•".repeat(value.length);
    const firstChars = value.slice(0, 4);
    const lastChars = value.slice(-4);
    return `${firstChars}${"•".repeat(Math.max(0, value.length - 8))}${lastChars}`;
  };

  const handleEditClick = () => {
    if (selectedConfig) {
      setEditFormData({
        displayName: selectedConfig.displayName,
        token: "", // User must re-enter token
        tokenSecret: "", // User must re-enter token secret
        restletUrl: selectedConfig.restletUrl
      });
      setIsEditing(true);
    }
  };


  const handleSaveEdit = async () => {
    if (!selectedConfig) return;

    if (!editFormData.token || !editFormData.tokenSecret) {
      toast.displayToast(
        Severity.Error,
        "Please enter both token and token secret to update credentials"
      );
      return;
    }

    setSending(true);
    try {
      const response = await api.messaging.post(
        `NetSuite/Auth/UpdateCredentials/${selectedConfig.id}`,
        {
          displayName: editFormData.displayName,
          company: selectedConfig.company,
          token: editFormData.token,
          tokenSecret: editFormData.tokenSecret,
          restletUrl: editFormData.restletUrl
        }
      );

      if (response.ok) {
        props.onChange(
          selectedConfig.id,
          "displayName",
          editFormData.displayName
        );
        props.onChange(selectedConfig.id, "token", editFormData.token);
        props.onChange(
          selectedConfig.id,
          "tokenSecret",
          editFormData.tokenSecret
        );
        props.onChange(
          selectedConfig.id,
          "restletUrl",
          editFormData.restletUrl
        );
        setIsEditing(false);

        const timer = setTimeout(() => {
          toast.displayToast(Severity.Success, "Account updated successfully! Audit log entry created.");
          clearTimeout(timer);
        }, 3000);
      } else {
        toast.displayToast(
          Severity.Error,
          "Failed to update credentials. Please verify the tokens are correct."
        );
      }
    } catch (error) {
      console.error("Failed to update credentials", error);
      toast.displayToast(
        Severity.Error,
        "Failed to update credentials. Please try again."
      );
    }
    setSending(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      displayName: "",
      token: "",
      tokenSecret: "",
      restletUrl: ""
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditing) return;

    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(true);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(true);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(true);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(true);
    }

    setTimeout(() => {
      let length = 0;

      if (input === displayNameRef.current) {
        length = editFormData.displayName.length;
      } else if (input === tokenInputRef.current) {
        length = editFormData.token.length;
      } else if (input === tokenSecretInputRef.current) {
        length = editFormData.tokenSecret.length;
      } else if (input === restletUrlInputRef.current) {
        length = editFormData.restletUrl.length;
      }

      input.setSelectionRange(length, length);
    }, 0);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(false);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(false);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(false);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(false);
    }
  };

  const handleToggleTokenVisibility = () => {
    setShowToken(!showToken);
    if (!showToken && tokenInputRef.current) {
      setTimeout(() => {
        tokenInputRef.current?.focus();
        const length = isEditing ? editFormData.token.length : (selectedConfig?.token || "").length;
        tokenInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  const handleToggleTokenSecretVisibility = () => {
    setShowTokenSecret(!showTokenSecret);
    if (!showTokenSecret && tokenSecretInputRef.current) {
      setTimeout(() => {
        tokenSecretInputRef.current?.focus();
        const length = isEditing ? editFormData.tokenSecret.length : (selectedConfig?.tokenSecret || "").length;
        tokenSecretInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  async function handleSubmit() {
    setSending(true);
    const response = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
      tenantId: tenantId,
      displayName: displayName,
      company: company,
      token: token,
      tokenSecret: tokenSecret,
      restletUrl: restletUrl,
      teamId: props.teamId
    });
    setSuccess(response.ok);
    if (response.ok) {
      props.addConfig();
      setCompany("");
      setDisplayName("");
      setToken("");
      setTokenSecret("");
      setRestletUrl("");
      setShowSetupModal(false);
    }
    setSending(false);
  }

  return (
    <div
      className={`${
        props.onCreateMode
          ? ""
          : "panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6"
      }`}
    >
      {!props.onCreateMode && (
        <div className="flex justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-800">Accounts</h2>
          </div>
          <div>
            <PrimaryBlueButton
              label="Add Account"
              onClick={() =>
                navigate(`/integrations/netsuite/netsuitecreate`, {
                  state: { teamId: props.teamId }
                })
              }
              prefixIcon={React.createElement(IconPlus)}
              className="w-48  h-full font-medium"
            />
          </div>
        </div>
      )}
      <table className="table-fixed w-full p-4">
        <thead>
          <tr>
            <td
              className={`text-slate-800 text-sm font-medium text-left pr-6 ${
                props.onCreateMode ? "w-20" : "w-25"
              }`}
            >
              Config ID
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Display Name
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              NetSuite Company ID
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Token Access
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Token Secret
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Restlet URL
            </td>
            {!props.onCreateMode && <td className="w-25"></td>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="pr-6 w-full">
              <input
                type="text"
                value={selectedConfig?.id || ""}
                className="w-full px-3 py-2 border rounded-lg text-slate-800 text-sm font-normal"
                disabled
              />
            </td>
            <td className="pr-6 w-full">
              {isEditing ? (
                <div className="relative">
                  <input
                    ref={displayNameRef}
                    type="text"
                    value={editFormData.displayName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        displayName: e.target.value
                      })
                    }
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg text-slate-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${displayNameFocused ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
                    <i className="fa-solid fa-pen text-xs"></i>
                  </span>
                </div>
              ) : props.configs.length > 1 ? (
                <div ref={dropdownRef} className="relative w-full">
                  <button
                    className="w-full text-sm font-normal bg-white border border-gray-300 rounded-lg px-4 py-2 text-left focus:ring-1 focus:ring-[#1670e7] focus:border-[#1670e7]"
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    {props.configs.find(
                      (config) => config.id === selectedConfigId
                    )?.displayName || "Select an option"}
                  </button>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <i
                      className={`transition fa-solid ${
                        isOpen ? "fa-angle-up" : "fa-angle-down"
                      } text-slate-400 mr-1`}
                    ></i>
                  </div>
                  {isOpen && (
                    <ul className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2">
                      {props.configs.map((config) => (
                        <li
                          key={config.id}
                          className={`flex justify-between items-center rounded-lg px-3 py-3 hover:bg-slate-100
                            text-sm font-normal hover:text-slate-800 hover:rounded-lg cursor-pointer group gap-2 mb-2
                            ${
                              config.id === selectedConfigId
                                ? "text-[#3383ec] bg-slate-100"
                                : "text-slate-800"
                            }`}
                          onClick={() => handleSelect(config)}
                        >
                          <span>{config.displayName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="px-3 py-2 border rounded-lg text-slate-800 text-sm font-normal h-10">
                  <span>{selectedConfig?.displayName}</span>
                </div>
              )}
            </td>
            <td className="pr-6 w-full">
              <input
                type="text"
                value={selectedConfig?.company || ""}
                className="w-full px-3 py-2 border rounded-lg text-slate-800 text-sm font-normal"
                disabled
              />
            </td>
            <td className="pr-6 w-full">
              <div className="relative">
                <input
                  ref={tokenInputRef}
                  type="text"
                  value={
                    isEditing
                      ? editFormData.token
                      : showToken
                      ? partiallyMaskValue(selectedConfig?.token || "")
                      : maskValue(selectedConfig?.token || "")
                  }
                  onChange={(e) =>
                    isEditing &&
                    setEditFormData({ ...editFormData, token: e.target.value })
                  }
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder={isEditing ? "Enter new token" : ""}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg text-slate-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${tokenFocused && isEditing ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
                  disabled={!isEditing}
                />
                {isEditing ? (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
                    <i className="fa-solid fa-pen text-xs"></i>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleToggleTokenVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <i
                      className={`fa-solid ${
                        showToken ? "fa-eye-slash" : "fa-eye"
                      }`}
                    ></i>
                  </button>
                )}
              </div>
            </td>
            <td className="pr-6 w-full">
              <div className="relative">
                <input
                  ref={tokenSecretInputRef}
                  type="text"
                  value={
                    isEditing
                      ? editFormData.tokenSecret
                      : showTokenSecret
                      ? partiallyMaskValue(selectedConfig?.tokenSecret || "")
                      : maskValue(selectedConfig?.tokenSecret || "")
                  }
                  onChange={(e) =>
                    isEditing &&
                    setEditFormData({
                      ...editFormData,
                      tokenSecret: e.target.value
                    })
                  }
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder={isEditing ? "Enter new token secret" : ""}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg text-slate-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${tokenSecretFocused && isEditing ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
                  disabled={!isEditing}
                />
                {isEditing ? (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
                    <i className="fa-solid fa-pen text-xs"></i>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleToggleTokenSecretVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <i
                      className={`fa-solid ${
                        showTokenSecret ? "fa-eye-slash" : "fa-eye"
                      }`}
                    ></i>
                  </button>
                )}
              </div>
            </td>

            <td className="w-full">
              <div className="relative">
                <input
                  ref={restletUrlInputRef}
                  type="text"
                  value={
                    isEditing
                      ? editFormData.restletUrl
                      : selectedConfig?.restletUrl || ""
                  }
                  onChange={(e) =>
                    isEditing &&
                    setEditFormData({
                      ...editFormData,
                      restletUrl: e.target.value
                    })
                  }
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className={`w-full px-3 py-2 ${isEditing ? 'pr-10' : 'pr-3'} border rounded-lg text-slate-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none ${restletUrlFocused && isEditing ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
                  disabled={!isEditing}
                />
                {isEditing && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none">
                    <i className="fa-solid fa-pen text-xs"></i>
                  </span>
                )}
              </div>
            </td>
            {!props.onCreateMode && (
              <td className="text-right pl-6">
                <div className="flex justify-end gap-2">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="hover:bg-slate-100 rounded-lg transition-colors px-1 py-2"
                        title="Save"
                        disabled={sending}
                      >
                        <IconCheckMark className="w-8 text-slate-800" />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="hover:bg-slate-100 rounded-lg transition-colors px-1 py-2"
                        title="Cancel"
                        disabled={sending}
                      >
                        <IconClose className="w-8 text-slate-800" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleEditClick}
                        className="hover:bg-slate-100 rounded-lg transition-colors px-1 py-2"
                        title="Edit"
                      >
                        <IconEditPencil2 className="w-8 text-slate-800" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedConfig) {
                            handleDeleteClick(
                              selectedConfig.id,
                              selectedConfig.displayName
                            );
                          }
                        }}
                        className="hover:bg-slate-100 rounded-lg transition-colors px-1 py-2 "
                        title="Delete"
                      >
                        <IconTrash2 className="w-8 text-slate-800" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            )}
          </tr>
        </tbody>
      </table>

      {isModalOpen && (
        <Modal show={isModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8  text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Remove Account
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>

            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to remove the account{" "}
              {configToDelete?.displayName}? This cannot be undone and will remove all mapped fields.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(configToDelete?.id)}
                className="rounded-lg px-6 py-2 bg-cornflower-blue-500 border border-cornflower-blue-500 hover:bg-cornflower-blue-400 hover:border-cornflower-blue-400 text-white text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
